// src/pages/Login.jsx
// git commit: "fix(Login): stronger retry timing to survive brief server restart gaps"
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/api";
import { toast } from "react-toastify";
import { Moon, Sun, ArrowLeft, Eye, EyeOff } from "lucide-react";

// Retry helper — retries on network/5xx errors, NOT on 400/401 (those are
// real answers, not transient failures). 3 attempts with growing delay
// gives the ~1-2s shared-hosting restart gap enough room to clear.
async function postWithRetry(url, data, maxRetries = 3) {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const res = await api.post(url, data);
      return res;
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      const shouldRetry = !err.response || status >= 500;

      if (!shouldRetry || attempt === maxRetries) {
        throw err;
      }

      await new Promise((r) => setTimeout(r, 1200 * attempt));
      console.log(`[Login] Retrying attempt ${attempt + 1}...`);
    }
  }
  throw lastError;
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [message, setMessage] = useState("");
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    const enabled = saved ? saved === "dark" : prefersDark;
    setDarkMode(enabled);
    document.documentElement.classList.toggle("dark", enabled);
  }, []);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };
  const [verifiedNotice, setVerifiedNotice] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);

  // Ref guard prevents double submit — important on iPhone where
  // the virtual keyboard sometimes triggers two form submits
  const isSubmittingRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("verified") === "1") setVerifiedNotice(true);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();

    // Double-submit guard
    if (isSubmittingRef.current || requesting) return;

    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();

    if (!cleanEmail || !cleanPassword) {
      setMessage("Email and password are required");
      return;
    }

    isSubmittingRef.current = true;
    setRequesting(true);
    setMessage("");
    setUnverifiedEmail(null);

    try {
      const res = await postWithRetry("/user/login", {
        email: cleanEmail,
        password: cleanPassword,
      });

      localStorage.setItem("accessToken", res.data.accessToken);
      localStorage.setItem("refreshToken", res.data.refreshToken);
      if (res.data.user) {
        localStorage.setItem("user", JSON.stringify(res.data.user));
      }
      sessionStorage.setItem("pp_just_logged_in", "1");

      toast.success("Login successful");
      navigate("/dashboard");
    } catch (err) {
      const status = err.response?.status;

      if (status === 400 || status === 401) {
        setMessage("Invalid email or password. Please check and try again.");
      } else if (status === 429) {
        setMessage("Too many login attempts. Please wait a few minutes.");
      } else if (status === 503) {
        setMessage("Server is still starting up. Please try again in a few seconds.");
      } else if (!err.response) {
        // Network error — no response received at all
        setMessage(
          "Connection error. Please check your internet connection and try again."
        );
      } else {
        setMessage("Login failed. Please try again.");
      }
    } finally {
      setRequesting(false);
      isSubmittingRef.current = false;
    }
  };

  const inputClass =
    "w-full h-[42px] px-3 text-sm border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:opacity-60";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="w-full border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-gray-600 dark:text-gray-300 hover:text-purple-600 transition"
            >
              <ArrowLeft size={18} />
            </button>
            <Link to="/" className="flex items-center">
              <img
                src="/assets/icons/logo3.png"
                alt="LARRYDIGITALS"
                className="h-5 sm:h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md dark:invert dark:hue-rotate-180"
              />
            </Link>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="text-gray-600 dark:text-gray-300 hover:text-purple-600 transition"
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>

      {/* Form */}
      <main className="flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-[420px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-6">
          <div className="text-center mb-6">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Welcome Back
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Sign in to continue to your account
            </p>
          </div>

          {verifiedNotice && (
            <div className="mb-4 px-3 py-2.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400 text-center">
              Email confirmed! Please sign in.
            </div>
          )}
{message && (
            <div className="mb-4 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 text-center">
              {message}
              {unverifiedEmail && (
                <button
                  type="button"
                  disabled={resending}
                  onClick={async () => {
                    setResending(true);
                    try {
                      await api.post("/user/resend-verification", { email: unverifiedEmail });
                      toast.success("Verification email sent — check your inbox.");
                    } catch {
                      toast.error("Failed to resend. Please try again shortly.");
                    } finally {
                      setResending(false);
                    }
                  }}
                  className="block mx-auto mt-2 text-xs font-semibold text-purple-600 dark:text-purple-400 hover:underline disabled:opacity-60"
                >
                  {resending ? "Sending..." : "Resend verification email"}
                </button>
              )}
            </div>
          )}

          <form
            className="space-y-4"
            onSubmit={handleLogin}
            autoComplete="on"
            noValidate
          >
            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Email Address
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={requesting}
                required
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                inputMode="email"
                className={inputClass}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={requesting}
                  required
                  autoComplete="current-password"
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* Remember / Forgot */}
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                Remember me
              </label>
              <Link
                to="/forgot-password"
                className="text-purple-600 dark:text-purple-400 hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={requesting}
              className={`w-full h-[42px] text-sm font-medium rounded-md transition ${
                requesting
                  ? "bg-purple-300 cursor-not-allowed"
                  : "bg-purple-600 hover:bg-purple-700 active:bg-purple-800"
              } text-white`}
            >
              {requesting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-gray-600 dark:text-gray-400">
            Don&apos;t have an account?{" "}
            <Link
              to="/signup"
              className="text-purple-600 dark:text-purple-400 font-medium hover:underline"
            >
              Sign Up
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}